/**
 * Die Standardregeln, die jeder neue Auftraggeber bekommt — als reine Daten, damit Worker und
 * Oberfläche dieselbe Liste lesen: der Worker legt sie an (services/standardregeln.ts), die
 * Oberfläche erkennt an den Schlüsseln, ob ein Auftraggeber sie schon hat.
 *
 * Closer 10 %, Setter 5 %, Empfehlungsgeber 2,5 % vom Netto jeder Zahlung; hat die Firma den Lead
 * gestellt, die Hälfte für Closer und Setter. Ohne Vermerk ist ein Lead intern (`ne extern` erfüllt
 * auch NULL — engine/rules.ts, evalCondition); die volle Provision gibt es nur ausdrücklich extern,
 * per `lead_quelle` oder FinSuites-Tag (tenants.extern_tags).
 *
 * Die Schlüssel sind mit Absicht andere als die des Sheet-Imports (`STD_CLOSER_PCT` …): beide
 * Sätze dürfen nebeneinander existieren.
 */
export interface StandardBedingung {
  field: 'deal.lead_source'
  op: 'eq' | 'ne'
  value: 'extern'
}

export interface StandardRegel {
  key: string
  name: string
  role_code: 'CLOSER' | 'SETTER' | 'HOST'
  role_label: string
  leistung_text: string
  product_codes: null
  recipient_mode: 'deal_role'
  recipient_deal_role: 'closer' | 'setter' | 'host'
  amount_mode: 'pct_of_net'
  rate_bp: number
  applies_to: 'every_rate'
  conditions: StandardBedingung[]
  unassigned_policy: 'emit' | 'skip'
  sort_order: number
}

// Intern ist der Standard: ein Abschluss ohne Vermerk zählt als von der Firma gestellt (Bastian, 10.10.2026).
const EXTERN: StandardBedingung = { field: 'deal.lead_source', op: 'eq', value: 'extern' }
const INTERN: StandardBedingung = { field: 'deal.lead_source', op: 'ne', value: 'extern' }
const VERMITTLUNG = 'Vermittlungsprovision'

export const STANDARD_REGELN: readonly StandardRegel[] = [
  {
    key: 'CLOSER_JEDE_RATE', name: 'Closer 10 % — Lead selbst geholt',
    role_code: 'CLOSER', role_label: 'Closer', leistung_text: VERMITTLUNG, product_codes: null,
    recipient_mode: 'deal_role', recipient_deal_role: 'closer',
    amount_mode: 'pct_of_net', rate_bp: 1000, applies_to: 'every_rate',
    conditions: [EXTERN], unassigned_policy: 'emit', sort_order: 10,
  },
  {
    key: 'CLOSER_JEDE_RATE_INTERN', name: 'Closer 5 % — Lead von der Firma gestellt',
    role_code: 'CLOSER', role_label: 'Closer', leistung_text: VERMITTLUNG, product_codes: null,
    recipient_mode: 'deal_role', recipient_deal_role: 'closer',
    amount_mode: 'pct_of_net', rate_bp: 500, applies_to: 'every_rate',
    conditions: [INTERN], unassigned_policy: 'emit', sort_order: 11,
  },
  // Setter und Empfehlungsgeber: `skip` — ein Abschluss ohne Setter ist einer, der keinen hat, keine
  // Zeile in der Prüfliste. Der Closer dagegen fehlt nie mit Absicht, deshalb `emit` → „Rolle fehlt".
  {
    key: 'SETTER_JEDE_RATE', name: 'Setter 5 % — Lead selbst geholt',
    role_code: 'SETTER', role_label: 'Setter', leistung_text: VERMITTLUNG, product_codes: null,
    recipient_mode: 'deal_role', recipient_deal_role: 'setter',
    amount_mode: 'pct_of_net', rate_bp: 500, applies_to: 'every_rate',
    conditions: [EXTERN], unassigned_policy: 'skip', sort_order: 20,
  },
  {
    key: 'SETTER_JEDE_RATE_INTERN', name: 'Setter 2,5 % — Lead von der Firma gestellt',
    role_code: 'SETTER', role_label: 'Setter', leistung_text: VERMITTLUNG, product_codes: null,
    recipient_mode: 'deal_role', recipient_deal_role: 'setter',
    amount_mode: 'pct_of_net', rate_bp: 250, applies_to: 'every_rate',
    conditions: [INTERN], unassigned_policy: 'skip', sort_order: 21,
  },
  // Die dritte Rolle des Abschlusses heißt in der Datenbank `host` (BestLife nannte sie so: der
  // Podcast-Host, der den Kunden brachte). Für jeden anderen Auftraggeber ist es schlicht der
  // Empfehlungsgeber — so heißt sie in der Oberfläche, und so heißt sie auf der Rechnung.
  {
    key: 'EMPFEHLUNG_JEDE_RATE', name: 'Empfehlungsgeber 2,5 %',
    role_code: 'HOST', role_label: 'Empfehlungsgeber', leistung_text: VERMITTLUNG, product_codes: null,
    recipient_mode: 'deal_role', recipient_deal_role: 'host',
    amount_mode: 'pct_of_net', rate_bp: 250, applies_to: 'every_rate',
    conditions: [], unassigned_policy: 'skip', sort_order: 30,
  },
]

export const STANDARD_SCHLUESSEL: readonly string[] = STANDARD_REGELN.map((r) => r.key)
